import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Crown,
  ArrowRight,
  Check,
  Loader2,
  Copy,
  Facebook,
  Instagram,
  Twitter,
  MessageCircle,
  Link2,
  Share2,
  Sparkles,
  Gift,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import FAQ from "@/components/FAQ";
import {
  joinReferralProgram,
  lookupReferralCode,
  buildShareUrl,
} from "@/lib/referrals";
import { track } from "@/lib/ebook";

const MEMBER_KEY = "cc_legacy_member_code";

const PERKS = [
  {
    icon: Gift,
    title: "$50 store credit",
    body: "Earned every time a friend you refer completes their first paid order.",
  },
  {
    icon: Crown,
    title: "Free Sovereign upgrade",
    body: "Unlocked after 2 paid referrals — a complimentary Sovereign Canvas, on the house.",
  },
  {
    icon: Sparkles,
    title: "Early access",
    body: "First look at every new collection, including Hustle & Roar drops.",
  },
];

const FAQS = [
  {
    q: "How does the reward work?",
    a: "Share your unique link. When someone clicks it and completes a paid Crowned Canvas order, you earn $50 in store credit. Two paid referrals unlock a free Sovereign Canvas upgrade.",
  },
  {
    q: "When do I get my credit?",
    a: "Credits post automatically the moment a referred order is marked paid — no waiting on payouts or manual checks.",
  },
  {
    q: "Can I refer myself?",
    a: "No — the system blocks self-referrals (same email as the member). Referrals must be a different paying client.",
  },
  {
    q: "Where do I redeem my credit?",
    a: "Reply to your confirmation email or message us. We apply credits to your next order or convert them into a free Sovereign Canvas once you hit the threshold.",
  },
  {
    q: "Is there a limit?",
    a: "No cap. Stack credits, stack canvases. Legacy members earn for every paid referral.",
  },
];

const FAKE_HASH = (s = "") => s.replace(/(.{2}).+(@.+)/, "$1•••$2");

export default function LegacyMembers() {
  const [searchParams] = useSearchParams();
  const [member, setMember] = useState(null);
  const [loadingMember, setLoadingMember] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // On mount: load existing member from localStorage, OR from ?code=
  useEffect(() => {
    const codeFromUrl = (searchParams.get("code") || "").trim().toUpperCase();
    let code = codeFromUrl;
    if (!code) {
      try { code = (window.localStorage.getItem(MEMBER_KEY) || "").toUpperCase(); } catch (_) {}
    }
    if (!code) {
      setLoadingMember(false);
      return;
    }
    lookupReferralCode(code)
      .then((m) => {
        setMember(m);
        try { window.localStorage.setItem(MEMBER_KEY, m.code); } catch (_) {}
      })
      .catch(() => {
        try { window.localStorage.removeItem(MEMBER_KEY); } catch (_) {}
      })
      .finally(() => setLoadingMember(false));
    track("legacy_members_view", code ? "with-code" : "anonymous");
  }, [searchParams]);

  const handleJoin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const m = await joinReferralProgram(name, email);
      setMember(m);
      try { window.localStorage.setItem(MEMBER_KEY, m.code); } catch (_) {}
      toast.success("Welcome to Legacy Members.");
      track("legacy_members_join", "join-form", { code: m.code });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't join. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      <SEO
        title="Legacy Members | Crowned Canvas Creations Referral Program"
        description="Join Legacy Members — the Crowned Canvas Creations referral program. Share your link, earn $50 credit per paid referral, and unlock a free Sovereign Canvas."
        path="/legacy-members"
      />
      <Header />
      <main className="pt-24 md:pt-32">
        {/* HERO */}
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_15%_30%,rgba(130,73,55,0.3),transparent_60%)]" aria-hidden />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20">
            <div className="inline-flex items-center gap-2 overline text-[#D4AF37]">
              <Crown className="w-3.5 h-3.5" />
              Legacy Members Program
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[80px] leading-[1] mt-5 max-w-4xl">
              Build the brand<br />
              <span className="italic text-[#D4AF37]">with us.</span>
            </h1>
            <p className="text-white/75 mt-6 max-w-2xl font-light leading-relaxed text-base md:text-lg">
              Crowned Canvas isn't just artwork — it's a movement. Refer a
              friend who orders a canvas, and we'll reward you the way
              luxury brands should: with real value, not gimmicks.
            </p>
          </div>
        </section>

        {loadingMember ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
        ) : member ? (
          <MemberDashboard member={member} />
        ) : (
          <>
            <PerksGrid />
            <JoinForm
              name={name} setName={setName}
              email={email} setEmail={setEmail}
              submitting={submitting}
              onSubmit={handleJoin}
            />
          </>
        )}

        <HowItWorks />
        <FAQ items={FAQS} title="The fine print, plainly." testidPrefix="legacy-faq" />

        {/* Bottom CTA */}
        <section className="py-20 md:py-28 border-t border-white/5 bg-black/40">
          <div className="max-w-[900px] mx-auto px-6 text-center">
            <p className="overline">Closing The Circle</p>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">
              The brand is the <span className="italic text-[#D4AF37]">community.</span>
            </h2>
            <p className="text-white/65 mt-5 font-light leading-relaxed max-w-xl mx-auto">
              Every referral helps a Black-owned legacy brand grow. Every reward we share goes back to the people who built us.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-9">
              <Link to="/#collection" className="inline-flex items-center gap-3 border border-white/20 text-white/85 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
                Browse The Collection<ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PerksGrid() {
  return (
    <section data-testid="legacy-perks" className="py-16 md:py-24 border-t border-white/5 bg-black/40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-10">
          <p className="overline">Member Perks</p>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05] max-w-2xl">
            What you earn.
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-0 border-t border-white/10">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="p-8 md:p-10 border-b sm:border-b-0 sm:border-r border-white/10 last:border-r-0 hover:bg-white/[0.02] transition-colors">
                <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-xl md:text-2xl mt-5">{p.title}</h3>
                <p className="text-white/60 text-sm md:text-base mt-3 font-light leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function JoinForm({ name, setName, email, setEmail, submitting, onSubmit }) {
  return (
    <section data-testid="legacy-join" className="py-20 md:py-28 border-t border-white/5">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <div className="md:col-span-5">
            <p className="overline">Become A Member</p>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05]">
              Claim your <span className="italic text-[#D4AF37]">link.</span>
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-white/60 font-light leading-relaxed self-end">
            Drop your name + email. We'll generate your unique referral link. Share it with the right people — we'll handle the rest.
          </p>
        </div>
        <form onSubmit={onSubmit} data-testid="legacy-join-form" className="bg-black/40 backdrop-blur-xl border border-white/10 p-7 md:p-10 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2.5">
              <Label className="overline text-white/60">Full name</Label>
              <Input
                required
                data-testid="legacy-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="bg-black border-white/15 h-14 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
            <div className="space-y-2.5">
              <Label className="overline text-white/60">Email</Label>
              <Input
                required
                type="email"
                data-testid="legacy-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="bg-black border-white/15 h-14 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="legacy-submit"
            className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Generate My Link<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
          </button>
          <p className="text-xs text-white/40">No subscription. No spam. Just your link + reward updates.</p>
        </form>
      </div>
    </section>
  );
}

function MemberDashboard({ member }) {
  const shareUrl = buildShareUrl(member.code);
  const message = `Crowned Canvas Creations — luxury portraits & canvas artistry for legacy builders. Use my link:`;

  const handleCopy = async (text = shareUrl, label = "Link copied.") => {
    track("referral_share", "copy", { code: member.code });
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch (_) {
      toast.error("Could not copy.");
    }
  };

  const handleShare = (platform) => {
    track("referral_share", platform, { code: member.code });
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(message);
    let href = "";
    if (platform === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`;
    else if (platform === "twitter") href = `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    else if (platform === "whatsapp") href = `https://api.whatsapp.com/send?text=${t}%20${u}`;
    if (href) window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const handleInstagram = async () => {
    track("referral_share", "instagram", { code: member.code });
    if (navigator.share) {
      try {
        await navigator.share({ title: "Crowned Canvas Creations", text: message, url: shareUrl });
        return;
      } catch (_) {}
    }
    handleCopy(`${message} ${shareUrl}`, "Link copied — paste it into Instagram.");
  };

  const handleNativeShare = async () => {
    track("referral_share", "native", { code: member.code });
    if (navigator.share) {
      try { await navigator.share({ title: "Crowned Canvas Creations", text: message, url: shareUrl }); return; } catch (_) {}
    }
    handleCopy();
  };

  const paid = member.total_paid_referrals || 0;
  const toUnlock = Math.max(0, 2 - paid);
  const progress = Math.min(1, paid / 2);

  return (
    <section data-testid="legacy-dashboard" className="py-12 md:py-20">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 space-y-10">
        {/* Welcome */}
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-7 md:p-10">
          <p className="overline text-[#D4AF37]">Welcome back</p>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
            {member.name || "Legacy Member"}, <span className="italic text-[#D4AF37]">you're in.</span>
          </h2>
          <p className="text-white/65 mt-3 text-sm md:text-base font-light">
            Member since {new Date(member.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" })} — your link is ready.
          </p>
        </div>

        {/* Share link */}
        <div className="border border-white/10 p-7 md:p-10">
          <p className="overline">Your Referral Link</p>
          <div className="mt-5 grid md:grid-cols-12 gap-4 items-stretch">
            <div className="md:col-span-9 flex items-center gap-3 bg-black border border-white/15 px-5 py-4 overflow-hidden">
              <code data-testid="referral-link" className="text-[#D4AF37] text-sm md:text-base truncate flex-1">{shareUrl}</code>
              <button
                type="button"
                onClick={() => handleCopy()}
                data-testid="referral-copy"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <button
              type="button"
              onClick={handleNativeShare}
              data-testid="referral-native-share"
              className="md:col-span-3 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-5 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <ShareBtn testid="referral-share-facebook" Icon={Facebook} label="Facebook" onClick={() => handleShare("facebook")} />
            <ShareBtn testid="referral-share-instagram" Icon={Instagram} label="Instagram" onClick={handleInstagram} />
            <ShareBtn testid="referral-share-twitter" Icon={Twitter} label="X / Twitter" onClick={() => handleShare("twitter")} />
            <ShareBtn testid="referral-share-whatsapp" Icon={MessageCircle} label="WhatsApp" onClick={() => handleShare("whatsapp")} />
            <ShareBtn testid="referral-share-code" Icon={Link2} label={`Code: ${member.code}`} onClick={() => handleCopy(member.code, "Code copied.")} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          <StatTile label="Link clicks" value={member.total_clicks || 0} />
          <StatTile label="Friends started" value={member.total_referrals || 0} />
          <StatTile label="Paid referrals" value={member.total_paid_referrals || 0} highlight />
          <StatTile
            label="Store credit"
            value={`$${(member.credit_balance_usd || 0).toFixed(0)}`}
            highlight
          />
        </div>

        {/* Unlock progress */}
        <div className="border border-white/10 p-7 md:p-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="overline">Free Sovereign Unlock</p>
              <h3 className="font-serif-display text-2xl md:text-3xl mt-3">
                {member.free_sovereign_earned
                  ? "Unlocked — a Sovereign on us. 👑"
                  : toUnlock === 0
                    ? "You're at the threshold."
                    : `${toUnlock} paid referral${toUnlock === 1 ? "" : "s"} away.`}
              </h3>
            </div>
            {member.free_sovereign_earned ? (
              <span className="overline bg-[#D4AF37] text-black px-3 py-1.5">Reward Earned</span>
            ) : null}
          </div>
          <div className="mt-6 h-2 bg-white/5 overflow-hidden">
            <div
              className="h-full bg-[#D4AF37] transition-all duration-500"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <p className="text-white/55 text-sm mt-3 font-light">
            Earn $50 credit per paid referral. 2 paid referrals = a free Sovereign Canvas.
          </p>
        </div>

        {/* Public identity preview */}
        <div className="border border-white/10 bg-black/40 p-7 md:p-10">
          <p className="overline">How Friends See You</p>
          <p className="text-white/55 text-sm mt-3 font-light">
            Anyone who clicks your link is welcomed to the Crowned Canvas experience —
            we never expose your email. Your link reads as:
          </p>
          <code className="block mt-4 text-[#D4AF37] text-sm break-all">{shareUrl}</code>
          <p className="text-white/40 text-xs mt-3">
            <span className="font-mono">{FAKE_HASH(member.email)}</span> · {member.code}
          </p>
        </div>
      </div>
    </section>
  );
}

function StatTile({ label, value, highlight }) {
  return (
    <div className={`bg-[#111111] p-6 md:p-7 ${highlight ? "border-l-2 border-[#D4AF37]" : ""}`}>
      <p className="overline text-white/55">{label}</p>
      <p className={`font-serif-display text-3xl md:text-4xl mt-3 ${highlight ? "text-[#D4AF37]" : "text-white"}`}>
        {value}
      </p>
    </div>
  );
}

function ShareBtn({ Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.18em] text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Claim your link", body: "Drop your name + email, get a unique Crowned Canvas referral link in seconds." },
    { n: "02", title: "Share with intention", body: "Send it to family, founders, friends, and anyone building their legacy." },
    { n: "03", title: "They order", body: "When they complete a Crowned Canvas order, the system tracks it automatically." },
    { n: "04", title: "You get rewarded", body: "$50 store credit per paid referral. 2 referrals unlocks a free Sovereign Canvas." },
  ];
  return (
    <section data-testid="legacy-how" className="py-20 md:py-28 border-t border-white/5">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <p className="overline">How It Works</p>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05]">
              Four steps to <span className="italic text-[#D4AF37]">earn.</span>
            </h2>
          </div>
        </div>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-white/10">
          {steps.map((s) => (
            <li key={s.n} className="p-7 md:p-8 border-b lg:border-b-0 lg:border-r border-white/10 last:border-r-0 hover:bg-white/[0.02] transition-colors">
              <span className="font-serif-display text-4xl text-[#D4AF37]/80">{s.n}</span>
              <h3 className="font-serif-display text-xl mt-6">{s.title}</h3>
              <p className="text-white/55 text-sm mt-2 font-light leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
